import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, MessageCircle } from "lucide-react";
import { Reveal, SectionHead } from "./Reveal";
import { SITE } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SERVICES = ["Ristrutturazione completa", "Bagno", "Cucina", "Serramenti", "Pavimenti e rivestimenti", "Rubinetteria", "Arredamento e cucine", "Scavi e movimento terra", "Altro"];
const EMPTY = { name: "", email: "", phone: "", service: SERVICES[0], message: "" };
const field = "w-full bg-transparent border-b border-[#d4a73c]/30 focus:border-[#e8c063] outline-none py-3 text-[#f4f1ea] placeholder:text-[#8f897e] transition-colors";

const Info = ({ icon: Icon, children, href, testid }) => {
  const Tag = href ? "a" : "div";
  return (
    <Tag href={href} data-testid={testid} className="flex gap-4 items-start text-[#e9e4da] hover:text-[#e8c063] transition-colors">
      <Icon size={18} className="mt-1 text-[#d4a73c] shrink-0" />
      <span className="leading-relaxed">{children}</span>
    </Tag>
  );
};

const ContactForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Richiesta inviata. Ti ricontatteremo al più presto.");
      setForm(EMPTY);
    } catch {
      toast.error("Invio non riuscito. Controlla i dati o scrivici su WhatsApp.");
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="contact-form" className="grid sm:grid-cols-2 gap-x-8 gap-y-6">
      <input required minLength={2} placeholder="Nome e cognome" value={form.name} onChange={set("name")} className={field} data-testid="contact-name-input" />
      <input required type="tel" minLength={6} placeholder="Telefono" value={form.phone} onChange={set("phone")} className={field} data-testid="contact-phone-input" />
      <input required type="email" placeholder="Email" value={form.email} onChange={set("email")} className={field} data-testid="contact-email-input" />
      <select value={form.service} onChange={set("service")} className={`${field} [&>option]:bg-[#151517]`} data-testid="contact-service-select">
        {SERVICES.map((s) => <option key={s}>{s}</option>)}
      </select>
      <textarea required minLength={5} rows={4} placeholder="Raccontaci il tuo progetto: zona, metratura, tempistiche…" value={form.message} onChange={set("message")}
        className={`${field} sm:col-span-2 resize-none`} data-testid="contact-message-input" />
      <button disabled={sending} data-testid="contact-form-submit-button"
        className="sm:col-span-2 justify-self-start mt-4 bg-[#d4a73c] text-[#0d0d0e] px-10 py-4 text-xs tracking-[0.25em] uppercase hover:bg-[#e8c063] disabled:opacity-60 transition-colors duration-500">
        {sending ? "Invio in corso…" : "Invia richiesta"}
      </button>
      <p data-testid="contact-privacy-note" className="sm:col-span-2 text-xs text-[#bdb7ab] leading-relaxed">
        Inviando la richiesta dichiari di aver letto l'<a href="/privacy-policy" className="text-[#e8c063] underline underline-offset-2">Informativa Privacy</a>.
        I dati saranno usati solo per ricontattarti.
      </p>
    </form>
  );
};

export const Contact = () => (
  <section id="contatti" data-testid="contact-section" className="relative bg-[#151517] py-28 lg:py-40">
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-16">
      <div className="lg:col-span-5">
        <SectionHead label="Contatti" title={<>Parliamo della <em className="italic text-[#e8c063]">tua casa.</em></>} />
        <Reveal delay={0.15} className="mt-12 space-y-6">
          <Info icon={Phone} href={SITE.phoneHref} testid="contact-phone-link">{SITE.phone}</Info>
          <Info icon={MessageCircle} href={SITE.whatsapp} testid="contact-whatsapp-link">Scrivici su WhatsApp</Info>
          <Info icon={Mail} href={`mailto:${SITE.email}`} testid="contact-email-link">{SITE.email}</Info>
          <Info icon={MapPin} testid="contact-address">{SITE.address}<br />{SITE.city}</Info>
          <Info icon={Clock} testid="contact-hours">
            {SITE.hours.map(([d, h]) => <span key={d} className="block">{d}: {h}</span>)}
          </Info>
        </Reveal>
      </div>
      <Reveal delay={0.2} className="lg:col-span-6 lg:col-start-7 border border-[#d4a73c]/20 p-8 md:p-12 bg-[#0d0d0e]/60">
        <p className="font-display text-3xl text-[#f4f1ea] mb-8">Richiedi un sopralluogo gratuito</p>
        <ContactForm />
      </Reveal>
    </div>
  </section>
);
